import { useRef, useState } from "react";
import { useAction } from "wasp/client/operations";
import { inviteStudentsBatch } from "wasp/client/operations";
import { CheckCircle, Upload, XCircle } from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";
import { useToast } from "../client/hooks/use-toast";
import { trackInvitationSent } from "../client/utils/posthog";
import { Badge } from "../client/components/ui/badge";
import { Button } from "../client/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../client/components/ui/dialog";
import {
  extractEmailsFromCSV,
  extractEmailsFromXLSX,
  type ImportedEmail,
} from "./emailImport";

// ── Types ──────────────────────────────────────────────────────────────────────

type EmailRow = ImportedEmail & {
  isDuplicate: boolean;
};

interface BulkImportModalProps {
  open: boolean;
  onClose: () => void;
  existingEmails: string[];
  onImported: () => void;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function BulkImportModal({
  open,
  onClose,
  existingEmails,
  onImported,
}: BulkImportModalProps) {
  const t = useTranslation();
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<EmailRow[]>([]);
  const [isSending, setIsSending] = useState(false);

  const inviteStudentsBatchFn = useAction(inviteStudentsBatch);

  const existingSet = new Set(existingEmails.map((e) => e.toLowerCase()));

  function applyDuplicateCheck(emails: ImportedEmail[]): EmailRow[] {
    const seenInBatch = new Set<string>();
    return emails.map((item) => {
      const dup = existingSet.has(item.email) || seenInBatch.has(item.email);
      if (!dup) seenInBatch.add(item.email);
      return { ...item, isDuplicate: dup };
    });
  }

  function handleFile(file: File) {
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";

    if (ext === "csv" || ext === "txt") {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setRows(applyDuplicateCheck(extractEmailsFromCSV(content)));
      };
      reader.readAsText(file);
      return;
    }

    if (ext === "xlsx" || ext === "xls") {
      const reader = new FileReader();
      reader.onload = (e) => {
        const buffer = e.target?.result as ArrayBuffer;
        setRows(applyDuplicateCheck(extractEmailsFromXLSX(buffer)));
      };
      reader.readAsArrayBuffer(file);
      return;
    }

    toast({
      title: t.mentor.errors.invalidEmail,
      description: "Format non supporté. Utilisez .csv, .xlsx ou .xls.",
      variant: "destructive",
    });
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = "";
  }

  const validRows = rows.filter((r) => r.isValid && !r.isDuplicate);

  async function handleSend() {
    if (!validRows.length) return;
    setIsSending(true);
    try {
      await inviteStudentsBatchFn({ emails: validRows.map((r) => r.email) });
      trackInvitationSent('bulk', validRows.length);
      toast({
        title: t.mentor.invite.confirmSend.replace("{count}", String(validRows.length)),
      });
      setRows([]);
      onImported();
      onClose();
    } catch (err: any) {
      toast({
        title: "Erreur",
        description: err.message ?? "Une erreur est survenue.",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  }

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) {
      setRows([]);
      onClose();
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t.mentor.invite.importTitle}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          {t.mentor.invite.importDescription}
        </p>

        {/* Drop zone */}
        <div
          role="button"
          tabIndex={0}
          aria-label="Zone de dépôt de fichier"
          className="cursor-pointer rounded-lg border-2 border-dashed border-border p-6 text-center transition-colors hover:border-primary focus-visible:border-primary focus-visible:outline-none"
          onClick={() => fileRef.current?.click()}
          onKeyDown={(e) => e.key === "Enter" && fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          <Upload className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Glissez un fichier ou cliquez pour parcourir
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            .csv, .xlsx — {t.mentor.invite.maxImport}
          </p>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls,.txt"
            className="hidden"
            onChange={handleInputChange}
          />
        </div>

        {/* Preview table */}
        {rows.length > 0 && (
          <div className="space-y-1">
            <p className="text-sm font-medium">
              {t.mentor.invite.preview} —{" "}
              <span className="text-muted-foreground">
                {rows.length} email(s) • {validRows.length} à inviter
              </span>
            </p>
            <div className="max-h-48 overflow-y-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <tbody>
                  {rows.map((row, i) => (
                    <tr
                      key={i}
                      className="flex items-center justify-between border-b border-border/50 px-3 py-1.5 last:border-0"
                    >
                      <span className="font-mono text-xs text-foreground/80 truncate max-w-[260px]">
                        {row.email || row.raw}
                      </span>
                      {row.isDuplicate ? (
                        <Badge
                          variant="outline"
                          className="shrink-0 gap-1 border-muted text-muted-foreground text-[10px]"
                        >
                          <XCircle className="h-3 w-3" />
                          {t.mentor.invite.alreadyInvited}
                        </Badge>
                      ) : !row.isValid ? (
                        <Badge
                          variant="outline"
                          className="shrink-0 gap-1 border-destructive/40 text-destructive text-[10px]"
                        >
                          <XCircle className="h-3 w-3" />
                          Email invalide
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="shrink-0 gap-1 border-teal-500/40 text-teal-400 text-[10px]"
                        >
                          <CheckCircle className="h-3 w-3" />
                          {t.mentor.invite.willBeInvited}
                        </Badge>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => handleOpenChange(false)}>
            Annuler
          </Button>
          <Button
            onClick={handleSend}
            disabled={!validRows.length || isSending}
          >
            {t.mentor.invite.confirmSend.replace("{count}", String(validRows.length))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
